import React from 'react';
import { Document, Text, StyleSheet } from '@react-pdf/renderer';
import { Reconciliation, ReconciliationLine } from '../../../types/finance';
import { formatPaisa } from '../../../utils/paisa';
import {
  Page, StatStrip, DataTable, pdfColors, pdfSpacing, fmtDate,
  type DataTableColumn, type SchoolBranding,
} from '../../../utils/pdf';
import { reconciliationStatement } from '../cashBank/reconcile/reconciliationStatement';
import StatementTable from './StatementTable';
import { Amount, TableTitle, figureWidth, leadColumns } from './movementCells';

interface Props {
  rec: Reconciliation;
  currency: string;
  branding: SchoolBranding;
  generatedAt: string;
}

const styles = StyleSheet.create({
  warning: { marginTop: pdfSpacing.sm, fontSize: 8, color: pdfColors.danger },
});

/**
 * A bank reconciliation — the statement balance carried to the books
 * through what was outstanding on the statement date, then the lines behind
 * each figure: those still outstanding, and those cleared on this statement.
 */
const ReconciliationDocument: React.FC<Props> = ({ rec, currency, branding, generatedAt }) => {
  const s = rec.summary;
  const money = (paisa: number) => formatPaisa(paisa, currency);
  const statementDate = fmtDate(rec.statementDate);
  const figureW = figureWidth(rec.lines.flatMap(l => [l.inPaisa, l.outPaisa]).map(money));
  const columns: Array<DataTableColumn<ReconciliationLine>> = [
    ...leadColumns<ReconciliationLine>(),
    { key: 'memo', header: 'Description', flex: 1, render: l => l.memo },
    { key: 'in', header: 'In', width: figureW, align: 'right', render: l => <Amount paisa={l.inPaisa} currency={currency} tone="in" /> },
    { key: 'out', header: 'Out', width: figureW, align: 'right', render: l => <Amount paisa={l.outPaisa} currency={currency} tone="out" /> },
  ];
  const table = (cleared: boolean, emptyText: string) => (
    <DataTable columns={columns} rows={rec.lines.filter(l => l.cleared === cleared)} zebra compact small emptyText={emptyText} />
  );

  return (
    <Document title={`Bank Reconciliation — ${rec.accountCode} ${rec.accountName}`} author={branding.name}>
      <Page
        branding={branding}
        meta={{ title: 'Bank Reconciliation', subtitle: `${rec.accountCode} ${rec.accountName}  ·  ${statementDate}`, generatedAt }}
        official={{ details: [
          { label: 'Account', value: `${rec.accountCode} ${rec.accountName}` },
          { label: 'Statement date', value: statementDate },
          { label: 'Status', value: rec.status === 'COMPLETED' ? `Completed${rec.completedByName ? ` by ${rec.completedByName}` : ''}` : 'In progress' },
          { label: 'Generated', value: fmtDate(generatedAt) },
        ], perRow: 2 }}
      >
        <StatStrip
          dense
          items={[
            { label: 'Statement balance', value: money(s.statementBalancePaisa), tone: 'primary' },
            { label: 'Deposits in transit', value: money(s.depositsInTransitPaisa), tone: 'success', weight: 1.05 },
            { label: 'Unpresented payments', value: money(s.unpresentedPaymentsPaisa), tone: 'danger', weight: 1.15 },
            { label: 'Balance per books', value: money(s.bookBalancePaisa), tone: 'primary' },
          ]}
        />
        <StatementTable
          title="Reconciliation statement"
          columns={['Amount']}
          currency={currency}
          lines={reconciliationStatement(rec, fmtDate).map(r => ({
            kind: r.kind ?? 'account', label: r.label, hint: r.hint, depth: r.kind ? 0 : 1, amounts: [r.paisa],
          }))}
        />
        {s.differencePaisa !== 0 && (
          <Text style={styles.warning}>
            Not reconciled: the statement is {s.differencePaisa > 0 ? 'higher' : 'lower'} than the cleared balance by {money(Math.abs(s.differencePaisa))}.
          </Text>
        )}
        <TableTitle>Outstanding items</TableTitle>
        {table(false, 'Nothing outstanding — every line has cleared.')}
        <TableTitle>Cleared on this statement</TableTitle>
        {table(true, 'No lines cleared yet.')}
      </Page>
    </Document>
  );
};

export default ReconciliationDocument;
